/**
 * Pastille d'icône OS (iOS / Android / Badging API web).
 *
 * Natif : `@capawesome/capacitor-badge` en import dynamique (chunk séparé).
 * Web : `navigator.setAppBadge` / `clearAppBadge` si dispo (PWA nice-to-have).
 * Échecs silencieux — ne doit jamais casser le flux métier.
 */

import { Capacitor } from '@capacitor/core';

export type BadgePluginLike = {
  set: (options: { count: number }) => Promise<void>;
  clear: () => Promise<void>;
  checkPermissions?: () => Promise<{ display: string }>;
  requestPermissions?: () => Promise<{ display: string }>;
  isSupported?: () => Promise<{ isSupported: boolean }>;
};

let badgePluginPromise: Promise<BadgePluginLike | null> | null = null;
let permissionEnsured = false;
/** Injection tests uniquement. */
let badgePluginOverride: BadgePluginLike | null | undefined;

async function getBadgePlugin(): Promise<BadgePluginLike | null> {
  if (badgePluginOverride !== undefined) {
    return badgePluginOverride;
  }
  if (!Capacitor.isNativePlatform()) return null;
  if (!badgePluginPromise) {
    badgePluginPromise = import('@capawesome/capacitor-badge')
      .then((m) => m.Badge as BadgePluginLike)
      .catch((err) => {
        console.warn('[AppBadge] Plugin indisponible:', err);
        return null;
      });
  }
  return badgePluginPromise;
}

async function ensureBadgePermission(Badge: BadgePluginLike): Promise<boolean> {
  if (permissionEnsured) return true;
  if (!Badge.checkPermissions || !Badge.requestPermissions) {
    permissionEnsured = true;
    return true;
  }
  try {
    let perm = await Badge.checkPermissions();
    if (perm.display !== 'granted') {
      perm = await Badge.requestPermissions();
    }
    permissionEnsured = perm.display === 'granted';
    return permissionEnsured;
  } catch {
    return false;
  }
}

async function setWebBadge(count: number): Promise<void> {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (!nav) return;
  try {
    if (count <= 0) {
      if (typeof nav.clearAppBadge === 'function') {
        await nav.clearAppBadge();
      }
      return;
    }
    if (typeof nav.setAppBadge === 'function') {
      await nav.setAppBadge(count);
    }
  } catch {
    /* ignore */
  }
}

/** Pose le compteur pastille (0 = clear). */
export async function setAppBadgeCount(count: number): Promise<void> {
  const n = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;

  const Badge = await getBadgePlugin();
  if (Badge) {
    try {
      const ok = await ensureBadgePermission(Badge);
      if (!ok) return;
      if (n <= 0) {
        await Badge.clear();
      } else {
        await Badge.set({ count: n });
      }
      return;
    } catch (err) {
      console.warn('[AppBadge] set/clear natif échoué:', err);
    }
  }

  await setWebBadge(n);
}

export async function clearAppBadgeCount(): Promise<void> {
  await setAppBadgeCount(0);
}

/** Reset caches (tests). */
export function _resetAppBadgeServiceForTests(): void {
  badgePluginPromise = null;
  permissionEnsured = false;
  badgePluginOverride = undefined;
}

/** Injection plugin (tests). */
export function _setBadgePluginForTests(plugin: BadgePluginLike | null): void {
  badgePluginOverride = plugin;
  permissionEnsured = false;
}
